import { useEffect, useMemo, useState } from "react";
import { fetchThrift, isKidsThrift } from "@/lib/lamka";
import { useAuth } from "@/context/AuthContext";
import { useLikeToggle } from "@/hooks/useLikeToggle";
import { useThriftCutouts } from "@/hooks/useThriftCutouts";
import { previousThriftVisit } from "@/lib/thriftVisit";
import { ThriftRail } from "./ThriftRail";

const Heading = ({ eyebrow, title, testId }) => (
  <div className="px-4 text-center mb-10">
    <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.35em] text-[#0F0F0F]/55 mb-3">{eyebrow}</p>
    <h2 data-testid={testId} className="font-display text-4xl sm:text-5xl text-[#B38F46] tracking-wide">{title}</h2>
  </div>
);

export const ThriftCloset = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [since] = useState(previousThriftVisit);
  const { user, loading: authLoading } = useAuth();
  const onToggleLike = useLikeToggle(setItems);
  const cutoutFor = useThriftCutouts(items.length);
  const grown = useMemo(() => items.filter((it) => !isKidsThrift(it)), [items]);
  const kids = useMemo(() => items.filter(isKidsThrift), [items]);

  useEffect(() => {
    if (authLoading) return;
    fetchThrift().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, [authLoading, user?.user_id]);

  const skeleton = <div data-testid="thrift-loading" className="h-[360px] mx-auto w-[170px] rounded-2xl bg-[#0F0F0F]/5 animate-pulse" />;

  return (
    <>
      <section id="thrift" data-testid="thrift-closet-section" className="isolate py-20 bg-[#EDE0CC] text-[#0F0F0F] overflow-hidden">
        <Heading eyebrow={<>Pre-loved <span className="mx-3 text-[#0F0F0F]/25">|</span> One of a kind</>} title="Thrift Closet" testId="thrift-closet-heading" />
        {loading ? skeleton : <ThriftRail items={grown} cutoutFor={cutoutFor} onToggleLike={onToggleLike} since={since} prefix="thrift" sizeKey="lamka_thrift_size" emptyText="No thrift pieces yet — check back soon." />}
      </section>
      <section id="thrift-kids" data-testid="thrift-kids-section" className="isolate pt-16 pb-20 bg-[#EDE0CC] text-[#0F0F0F] overflow-hidden border-t border-[#0F0F0F]/10">
        <Heading eyebrow={<>Pre-loved <span className="mx-3 text-[#0F0F0F]/25">|</span> Little ones</>} title="Kids Thrift" testId="thrift-kids-heading" />
        {loading ? null : <ThriftRail items={kids} cutoutFor={cutoutFor} onToggleLike={onToggleLike} since={since} prefix="thrift-kids" sizeKey="lamka_thrift_kids_age" byAge emptyText="No kids' pieces yet — check back soon." />}
      </section>
    </>
  );
};
